import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "Production by Month",
  "includeToc": true,
  "layout": "downloads",
  "description": "This dataset contains monthly production volumes on federal lands and waters and Native American lands.",
  "tags": ["Data", "Downloads", "Documentation", "Native American", "Indian", "Federal", "Production", "coal", "oil", "gas"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const DownloadDataFile = makeShortcode("DownloadDataFile");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/"
      }}>{`Downloads /`}</a></p>
    <h1 {...{
      "id": "Production-by-Month",
      "style": {
        "position": "relative"
      }
    }}>{`Production by Month`}</h1>
    <p>{`This dataset contains monthly production volumes for U.S. federal minerals on both land (onshore) and water (offshore) and Native American minerals. We have monthly production data from January 2003 through the most recently available month, which is usually 3-4 months prior to the current month.`}</p>
    <br />
    <p>{`Download production data by month:`}</p>
    <DownloadDataFile linkType="DownloadCsv" dataSet="monthly_production.csv" mdxType="DownloadDataFile">
  Monthly production
    </DownloadDataFile>
    <DownloadDataFile linkType="DownloadXls" dataSet="monthly_production.xlsx" mdxType="DownloadDataFile">
  Monthly production
    </DownloadDataFile>
    <h2 {...{
      "id": "Scope",
      "style": {
        "position": "relative"
      }
    }}>{`Scope`}</h2>
    <p>{`This dataset includes natural resource production for U.S. federal minerals for both onshore and offshore areas and Native American minerals. It does not include privately owned minerals or U.S. state minerals. The dataset includes data tracked and managed by the Department of the Interior’s Office of Natural Resources Revenue (ONRR). Monthly production data is available for coal, oil, and gas. The production data for oil and gas is collected on Form ONRR-4054-B (Oil and Gas Operations Report, Part B). Coal production is collected on Form ONRR-4430 (Solid Minerals Production and Royalty Report).`}</p>
    <h2 {...{
      "id": "Data-publication",
      "style": {
        "position": "relative"
      }
    }}>{`Data publication`}</h2>
    <p>{`We update this production data every month, but final production numbers aren't available for 3-4 months. For example, the most recent production data for a file updated in November will be from July or August.`}</p>
    <h2 {...{
      "id": "Monthly-totals-and-annual-totals",
      "style": {
        "position": "relative"
      }
    }}>{`Monthly totals and annual totals`}</h2>
    <p>{`The sum of 12-month production totals for a given calendar or fiscal year may not add up to the annual total found in the production by year data.
`}<a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/production/"
      }}>{`Production by year data`}</a>{`
While the production volume (amount of a commodity coming out of the ground) doesn't change, the way it is accounted for might. There are two main reasons why:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Operators may not report on time, meaning production for a past month might increase when the operator submits the report and ONRR updates the data.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The Bureau of Land Management may choose to change agreements and units after production. These changes may affect the allocation of the production volume, changing the data.`}</p>
      </li>
    </ul>
    <p>{`For example, changing an agreement retroactively may change the allocation of production among states, the federal government, and Native American tribes and individuals. As a result, the apportioned totals in the data would change.`}</p>
    <h2 {...{
      "id": "Data-dictionary",
      "style": {
        "position": "relative"
      }
    }}>{`Data dictionary`}</h2>
    <h3 {...{
      "id": "Fields-and-definitions",
      "style": {
        "position": "relative"
      }
    }}>{`Fields and definitions`}</h3>
    <p><strong parentName="p">{`Date`}</strong>{`  The date in which the production occurred.`}</p>
    <br />
    <p><strong parentName="p">{`Land Class`}</strong>{`  This field distinguishes federal lands and waters from Native American lands.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Federal`}</strong>{`  includes `}<a parentName="li" {...{
          "href": "https://fas.org/sgp/crs/misc/R42346.pdf"
        }}>{`Federal minerals`}</a>{` that are owned by or under the jurisdiction of the federal government, including: public domain, acquired, military acquired, and the `}<a parentName="li" {...{
          "href": "https://www.boem.gov/OCS-Lands-Act-History/"
        }}>{`Outer Continental Shelf`}</a>{`.`}</li>
      <li parentName="ul"><strong parentName="li">{`Native American`}</strong>{`  includes Tribal minerals held in trust by the federal government for a tribe’s use, and allotments held in trust by the federal government for individual Native American use.`}<br /></li>
    </ul>
    <p><strong parentName="p">{`Land Category`}</strong>{`  This field distinguishes between onshore and offshore production.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Onshore`}</strong>{`  Situated or occurring on land`}</li>
      <li parentName="ul"><strong parentName="li">{`Offshore`}</strong>{`  Submerged lands located farther than three miles off a state’s coastline, or three marine leagues into the Gulf of Mexico off Texas and Western Florida`}<br /></li>
    </ul>
    <p><strong parentName="p">{`Commodity`}</strong>{` We have monthly data for oil, gas, and coal, since these are high-volume commodities that result in the most revenue. This field includes the units for each commodity in parentheses.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Oil Prod Vol (bbl)`}</strong>{`  Oil production, measured in barrels (bbl)`}</li>
      <li parentName="ul"><strong parentName="li">{`Gas Prod Vol (Mcf)`}</strong>{`  Gas (natural gas), measured in thousand cubic feet (Mcf)`}</li>
      <li parentName="ul"><strong parentName="li">{`Coal Prod Vol (ton)`}</strong>{`  Coal is measured in tons. A ton is 2000 pounds.`}</li>
    </ul>
    <p><strong parentName="p">{`Volume`}</strong>{`  Shows the production volume (amount produced). The units for volume are shown in the preceding field (Commodity).`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      